import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { Subject, Observable, merge } from 'rxjs';
import { switchMap, map, filter, takeUntil, startWith } from 'rxjs/operators';

import { AmazingTimePickerService } from '@jonijnm/amazing-time-picker';

import { formatTime, validateTime } from '@library/common/functions';
import { BreakpointService, EnumsService } from '@library/core/services';
import { AwbTypes } from '@library/common/models';

import { RelativeDateService } from '@library/common/datehandler';

import { ShipmentService } from '../shipment.service';

import { StartDates } from './relative-dates';

@Component({
   selector: 'shipment-dates',
   templateUrl: './dates.component.html',
   styleUrls: ['./dates.component.scss'],
})
export class DatesComponent implements OnInit, OnDestroy {
   today = new Date();
   datePlaceholder = 'MM/DD/YYYY';

   servicelevelFilter = new FormControl('');
   filteredServicelevels: Observable<string[]>;

   awbTypes = AwbTypes;

   StartDates = StartDates;
   startDates = Object.keys( StartDates );

   dates = [ 'pdatefrom', 'pdateto', 'ddatefrom', 'ddateto' ];

   _onDestroy: Subject<void> = new Subject();

   formatTime = formatTime;
   validateTime = validateTime;

   settingValue = false;

   constructor(
      private atp: AmazingTimePickerService,
      public breakpoints: BreakpointService,
      public shipment: ShipmentService,
      public enums: EnumsService,
      public relativeDates: RelativeDateService,
   ) {
      this.today.setHours( 0 );
      this.today.setMinutes( 0 );
      this.today.setSeconds( 0 );
   }

   ngOnInit() {
      if ( this.shipment.template ) {
         // clear validators on template dates ( no validation for relative dates )
         this.dates.map( d => this.shipment.shipmentForm.get( d )).map( c => c.clearValidators() );
      } else {
         // translate possible relative values to actual dates ( or sanitize actual dates )
         this.dates.map( d => this.shipment.shipmentForm.get( d ))
            .map( c => c.value ? c.setValue( new Date( c.value )) : c );
      }

      if ( !this.shipment.template ) {
         this.dates.filter(date => date === 'pdatefrom' || date === 'ddatefrom')
            .forEach(date => {
               const control = this.shipment.shipmentForm.get(date);
               control.valueChanges.pipe(
                  takeUntil(this._onDestroy),
                  filter(_ => control.value),
                  filter(_ => !this.settingValue)
               ).subscribe(value => {
                  this.settingValue = true;
                  if (date === 'pdatefrom') {
                     const pdatetoControl = this.shipment.shipmentForm.get('pdateto');
                     pdatetoControl.setValue(new Date(value));
                     const ptimefromControl = this.shipment.shipmentForm.get('ptimefrom');
                     ptimefromControl.setValue( '08:00');
                     const ptimetoControl = this.shipment.shipmentForm.get('ptimeto');
                     ptimetoControl.setValue( '16:00');
                  } else if (date === 'ddatefrom') {
                     const ddatetoControl = this.shipment.shipmentForm.get('ddateto');
                     ddatetoControl.setValue(new Date(value));
                     const dtimefromControl = this.shipment.shipmentForm.get('dtimefrom');
                     dtimefromControl.setValue( '08:00');
                     const dtimetoControl = this.shipment.shipmentForm.get('dtimeto');
                     dtimetoControl.setValue( '16:00');
                  }
                  this.settingValue = false;
               });
            });
      }

      this.shipment.shipmentForm$.pipe(
         takeUntil( this._onDestroy ),
         filter( s => s.get( 'shipType' ).value && s.get( 'mode' ).value ),
         switchMap( s => this.enums.servicelevels( s.get( 'shipType' ).value, s.get( 'mode' ).value, true )),
      ).subscribe( levels => {
         if ( !levels.includes( this.shipment.shipmentForm.get( 'servicelevel' ).value )) {
            this.shipment.shipmentForm.get( 'servicelevel' ).setValue( '' );
         }
         if ( levels.length === 1 ) {
            this.shipment.shipmentForm.get( 'servicelevel' ).setValue( levels[0] );
         }
      });
      
      merge(
         this.shipment.shipmentForm$,
         this.servicelevelFilter.valueChanges
      ).pipe(
         map( _ => this.servicelevelFilter.value ),
         startWith( '' ),
         takeUntil( this._onDestroy ),
      ).subscribe( value => {
         value = value.toLowerCase().trim();

         this.filteredServicelevels = this.enums.servicelevels( this.shipment.shipType, this.shipment.mode, true ).pipe(
            map( levels => levels.filter( s => s?.toLowerCase().includes( value ))),
         );
      });
      
      this.datetimeValidation();
   }

   ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();
   }

   openClock( field: string ): void {
      const f = this.shipment.shipmentForm.get( field );
      let time = f.value || '00:00';
      
      if ( !this.validateTime( f.value )) { time = '00:00'; }

      const timepicker = this.atp.open({ time });
      if ( timepicker ) {
         timepicker.afterClose()
         .subscribe( t => f.setValue( t ));
      }
   }

   valid(): boolean {
      return ![ 'servicelevel', 'ptimefrom', 'ptimeto', 'dtimefrom', 'dtimeto',
      'pdatefrom', 'pdateto', 'ddatefrom', 'ddateto' ].filter( f =>
         this.shipment.shipmentForm.get( f ).invalid
      ).length;
   }

   datetimeValidation() {
      var ddatefrom = this.shipment.shipmentForm.get( 'ddatefrom' );
      var dtimefrom = this.shipment.shipmentForm.get( 'dtimefrom' );
      var pdatefrom = this.shipment.shipmentForm.get( 'pdatefrom' );
      var ptimefrom = this.shipment.shipmentForm.get( 'ptimefrom' );

      var ddateto = this.shipment.shipmentForm.get( 'ddateto' );
      var dtimeto = this.shipment.shipmentForm.get( 'dtimeto' );
      var pdateto = this.shipment.shipmentForm.get( 'pdateto' );
      var ptimeto = this.shipment.shipmentForm.get( 'ptimeto' );
      pdatefrom.addValidators([ this.DateValidation( pdatefrom, pdateto ),
         this.CrossDateValidation( pdatefrom, ddatefrom ),
         this.OverValidation( ddatefrom, pdatefrom, dtimefrom, ptimefrom ) ]);
      pdateto.addValidators([ this.DateValidation( pdatefrom, pdateto ),
         this.CrossDateValidation(pdateto, ddateto),
         this.OverValidation( ddateto, pdateto, dtimeto, ptimeto ) ]);
      ptimefrom.addValidators([ this.TimeValidation( pdatefrom, pdateto, ptimefrom, ptimeto ),
         this.OverValidation( ddatefrom, pdatefrom, dtimefrom, ptimefrom )]);
      ptimeto.addValidators([ this.TimeValidation( pdatefrom, pdateto, ptimefrom, ptimeto),
         this.OverValidation( ddateto, pdateto, dtimeto, ptimeto ) ]);
      ddatefrom.addValidators([ this.DateValidation( ddatefrom, ddateto ),
         this.CrossDateValidation( pdatefrom, ddatefrom ),
         this.OverValidation( ddatefrom, pdatefrom, dtimefrom, ptimefrom ) ]);
      ddateto.addValidators([ this.DateValidation( ddatefrom, ddateto ),
         this.CrossDateValidation(pdateto, ddateto),
         this.OverValidation( ddateto, pdateto, dtimeto, ptimeto ) ]);
      dtimefrom.addValidators([ this.TimeValidation( ddatefrom, ddateto, dtimefrom, dtimeto ),
         this.OverValidation( ddatefrom, pdatefrom, dtimefrom, ptimefrom ) ]);
      dtimeto.addValidators([ this.TimeValidation( ddatefrom, ddateto, dtimefrom, dtimeto),
         this.OverValidation( ddateto, pdateto, dtimeto, ptimeto ) ]);

      merge(
         pdatefrom.valueChanges,
         pdateto.valueChanges,
         ptimefrom.valueChanges,
         ptimeto.valueChanges,
         ddatefrom.valueChanges,
         ddateto.valueChanges,
         dtimefrom.valueChanges,
         dtimeto.valueChanges
      ).subscribe( _ => {
         this.updateAllFields();
      });
      
   }

   updateAllFields() {
      let fields = [ 'ddatefrom', 'dtimefrom', 'pdatefrom', 'ptimefrom',
               'ddateto', 'dtimeto', 'pdateto', 'ptimeto' ];

      for( let field of fields) {
         this.shipment.shipmentForm.get( field ).updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
   }

   //Validation for Pickup Time From & To Or Delivery Time From & To
   TimeValidation(dateFrom: AbstractControl, dateTo: AbstractControl, timeFrom: AbstractControl, timeTo: AbstractControl): ValidatorFn {
      return (): ValidationErrors | null => {
         const datetimeFrom = this.getTime( timeFrom );

         const datetimeTo = this.getTime( timeTo );
          if( this.getDate(dateFrom) == this.getDate(dateTo) && datetimeFrom > datetimeTo ) {
            return { dateTimeFilter: true };
          }
          return null;
      };
   }

   //Validation for Pickup Date From & To || Delivery Date From & To
   DateValidation(dateFrom: AbstractControl, dateTo: AbstractControl): ValidatorFn {
      return (): ValidationErrors | null => {
          if( dateFrom.value <= dateTo.value ) {
            return null;
          }
          return { dateTimeFilter: true }
      };
   }

   //Validation for Pickup Date From & Delivery Date From || Pickup Date To & Delivery Date To
   CrossDateValidation(dateFrom1: AbstractControl, dateFrom2: AbstractControl): ValidatorFn {
      return (): ValidationErrors | null => {
          if( dateFrom1.value <= dateFrom2.value ) {
            return null;
          }
          return { dateTimeFilter: true }
      };
   }

   //Validation for all date and time fields
   OverValidation(dateFrom1: AbstractControl, dateFrom2: AbstractControl, timeFrom1: AbstractControl, timeFrom2: AbstractControl): ValidatorFn {
      return (): ValidationErrors | null => {
         const datetimeFrom1 = this.getTime( timeFrom1 );

         const datetimeFrom2 = this.getTime( timeFrom2 );
         
          if( this.getDate(dateFrom1) == this.getDate(dateFrom2) && datetimeFrom1 < datetimeFrom2 ) {
            return { dateTimeFilter: true };
          }
          return null;
      };
   }

   getTime( control: AbstractControl ) {
      const datetime = new Date();
      const [ hourFrom, minuteFrom, secondsFrom ] =  ( control.value ?? '' ).split( ':' );
   
      datetime.setHours( hourFrom ?? 0 );
      datetime.setMinutes( minuteFrom ?? 0 );
      datetime.setSeconds( secondsFrom ?? 0 );

      return datetime.getTime();
   }

   getDate( control: AbstractControl ) {
      const datetime = new Date( control.value );
      datetime.setHours( 0, 0, 0, 0 );

      return datetime.getTime();
   }
}
